/**
 * Registry of supported organization currencies.
 * To expand: add entries here and mirror backend Organization.settings.currency enum.
 */
export const CURRENCY_CONFIG = {
  VND: {
    label: 'đ',
    locale: 'vi-VN',
    decimals: 0,
    symbol: '₫',
  },
} as const;

export type OrgCurrency = keyof typeof CURRENCY_CONFIG;

export const SUPPORTED_CURRENCIES = Object.keys(CURRENCY_CONFIG) as OrgCurrency[];

export const DEFAULT_CURRENCY: OrgCurrency = 'VND';

export const resolveCurrency = (value?: string | null): OrgCurrency => {
  if (value && value in CURRENCY_CONFIG) {
    return value as OrgCurrency;
  }
  return DEFAULT_CURRENCY;
};

export const getCurrencyLabel = (currency: OrgCurrency): string =>
  CURRENCY_CONFIG[currency].label;

export const roundCurrencyAmount = (amount: number | null | undefined): number => {
  return Math.round(amount ?? 0);
};

export const formatCurrency = (
  amount: number | null | undefined,
  currency: OrgCurrency
): string => {
  const { locale, decimals } = CURRENCY_CONFIG[currency];
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(roundCurrencyAmount(amount));
};

/** Compact money for dense UI triggers (e.g. "1,5 Tr", "500 N"). */
export const formatCompactCurrency = (
  amount: number | null | undefined,
  currency: OrgCurrency
): string => {
  const { locale } = CURRENCY_CONFIG[currency];
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(roundCurrencyAmount(amount));
};

/** Number only (no symbol) — useful for PDFs and compact displays */
export const formatAmount = (
  amount: number | null | undefined,
  currency: OrgCurrency
): string => {
  const { locale, decimals } = CURRENCY_CONFIG[currency];
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(roundCurrencyAmount(amount));
};

export const toAmountDigits = (raw: string): string => raw.replace(/\D/g, '');

export const countDigitsBefore = (value: string, index: number): number =>
  (value.slice(0, Math.max(0, index)).match(/\d/g) ?? []).length;

/** Caret index after the Nth digit in a formatted amount (or end if fewer digits). */
export const caretIndexFromDigitCount = (formatted: string, digitCount: number): number => {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (formatted[i] >= '0' && formatted[i] <= '9') {
      seen += 1;
      if (seen === digitCount) return i + 1;
    }
  }
  return formatted.length;
};

/**
 * Re-format a live amount field and map caret by digit count, not char index.
 * `selectionStart` should be the caret *after* the keystroke.
 */
export const formatLiveAmountInput = (
  raw: string,
  selectionStart: number | null,
  formatAmountFn: (amount: number) => string
): { value: string; caret: number } => {
  const digitCount = countDigitsBefore(raw, selectionStart ?? raw.length);
  const digits = toAmountDigits(raw);
  const value = digits === '' ? '' : formatAmountFn(Number(digits));
  return { value, caret: caretIndexFromDigitCount(value, digitCount) };
};

/** Parse a user-typed amount (formatted or raw digits) into a rounded number. */
export const parseAmountInput = (
  value: string,
  currency: OrgCurrency = DEFAULT_CURRENCY
): number => {
  const { decimals } = CURRENCY_CONFIG[currency];
  if (decimals === 0) {
    const digits = toAmountDigits(value);
    return roundCurrencyAmount(digits ? Number(digits) : 0);
  }

  const normalized = value.replace(/[^\d.,-]/g, '').replace(',', '.');
  return roundCurrencyAmount(Number.parseFloat(normalized) || 0);
};
